import { computed, type Ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { GET_NIGERIAN_STATES } from '@/graphql/referenceData';
import type { NigerianStateListQuery } from '@/types/operations';
import { allLgas, lgasForState } from '@/utils/nigerianStates';

/**
 * The Nigerian states and their LGAs (#574), behind the list-sourced custom
 * field dropdowns. The list never changes, so the cache is enough; `enabled`
 * lets a component that only sometimes needs it skip the request.
 */
export function useNigerianStates(options: { enabled?: Ref<boolean> } = {}) {
  const { result, loading, error } = useQuery<NigerianStateListQuery>(
    GET_NIGERIAN_STATES,
    null,
    () => ({ fetchPolicy: 'cache-first', enabled: options.enabled?.value ?? true }),
  );

  const states = computed(() => result.value?.nigerianStates ?? []);
  const stateNames = computed(() => states.value.map(s => s.name));

  return {
    states,
    stateNames,
    allLgas: computed(() => allLgas(states.value)),
    lgasFor: (state: string | null | undefined) => lgasForState(states.value, state),
    loading,
    error,
  };
}
